import { __stringify, BadImplementationException, exists } from '@nu-art/ts-common';
import { resolveInRangeConversion, resolveRangeEvaluationLogic } from './evaluation-logics/range.js';
import { resolveInSet, resolveInSetConversion } from './evaluation-logics/set.js';
import { resolveSelf } from './evaluation-logics/self.js';
import { resolveConversionBaseEvaluationLogic } from './evaluation-logics/generic-conversion.js';
import { resolveNotEqual, resolveNotEqualConversion } from './evaluation-logics/not-equal.js';
import { resolveBaseNormalRangeAtom, resolveNormalRange, resolveNormalRangeConversion } from './evaluation-logics/normal-range.js';
import { resolveExists } from './evaluation-logics/exists.js';
import { resolveNotExists } from './evaluation-logics/not-exists.js';
import { resolveIsChiefComplaint } from './evaluation-logics/is-chief-complaint.js';
import { AllowedOperators, EvaluationLogicTypes, InSetOperators, NumericDBValueTypes, RangeOperators, StringDBValueTypes } from './consts.js';
import { resolveAssociatedEntity } from './utils.js';
import { getBaseDeltaOperator, getDeltaTypeOperator, variableType_Attribute } from '../../../_entity.js';
import { AllDeltaLogic, BaseDeltaLogic, inNormalRangeOperators, LogicType_Exists, LogicType_NotExists, LogicType_IsChiefComplaint, LogicType_NotEquals, LogicType_NotInSet, SelfOperators } from '../../../_enum.js';
import { booleanExpressionTypes, numericExpressionTypes } from '../../../_entity/expression/consts.js';
export const convertAtomLevel = (dbAtom, dependencies) => {
    const convertedAtom = {};
    // Assign the convertedAtom metadata to the atom
    Object.assign(convertedAtom, resolveAtomMetaData(dbAtom, dependencies));
    // Handle atom conversion in case of single and multiple case
    if (dbAtom.cases.length > 1 || dbAtom.cases[0].value)
        conversionAtoms(dbAtom, convertedAtom, dependencies);
    else
        singleCaseAtoms(dbAtom, convertedAtom, dependencies);
    return convertedAtom;
};
// ########################## Metadata Resolving ##########################
const resolveAtomMetaData = (dbAtom, dependencies) => {
    const convertedAtomMetadata = {};
    // Resolve the atom type, can be expression (general score or function) or a hcs variable
    convertedAtomMetadata.type = resolveAtomType(dbAtom);
    // Add comment if exists, currently UI doesn't support
    if (exists(dbAtom.comment))
        convertedAtomMetadata.comment = dbAtom.comment;
    // Resolve related entity from the dependency object
    const associatedEntity = resolveAssociatedEntity(dbAtom, dependencies);
    if (associatedEntity?.type === variableType_Attribute)
        throw new BadImplementationException(`Cannot use ${associatedEntity.name} as left hand because it's an attribute`);
    if (associatedEntity) {
        if (dbAtom.leftHand?.type === 'expression') {
            convertedAtomMetadata.expression_db_id = associatedEntity._id;
            convertedAtomMetadata.name = associatedEntity.label;
        }
        else {
            convertedAtomMetadata.db_id = associatedEntity._id;
            convertedAtomMetadata.hv_name = associatedEntity.name;
        }
    }
    if (convertedAtomMetadata.type === 'delta-hv-atom') {
        const baseDeltaOp = getBaseDeltaOperator(getFirstCaseOperator(dbAtom));
        if (!baseDeltaOp)
            throw new BadImplementationException('delta atom must have base delta operator');
        convertedAtomMetadata.delta_type = baseDeltaOp?.includes('%') ? 'percent_diff' : 'diff';
        if (dbAtom.deltaTimeDiff)
            convertedAtomMetadata.time_diff_constraint = dbAtom.deltaTimeDiff;
    }
    const sampleInfo = dbAtom.sampleInfo;
    if (sampleInfo && (convertedAtomMetadata.type === 'hv-normal-range-atom' || convertedAtomMetadata.type === 'hv-atom')) {
        convertedAtomMetadata.sample = resolveSampleInfo(sampleInfo);
    }
    return convertedAtomMetadata;
};
const resolveSampleInfo = (sampleInfo) => {
    const convertedSample = {};
    // add expiry if exists
    if (exists(sampleInfo.expiry) && sampleInfo.expiry?.length)
        convertedSample.expiry = sampleInfo.expiry;
    // add time constraint is existing
    if (exists(sampleInfo.timeConstraint) && sampleInfo.timeConstraint?.length)
        convertedSample.time_constraint = sampleInfo.timeConstraint;
    // Convert string to numeric sample index
    switch (sampleInfo.sampleIndex) {
        case 'latest':
            convertedSample.index = sampleInfo.sampleIndex;
            break;
        case 'latest - 1':
            convertedSample.index = 1;
            break;
        case 'latest - 2':
            convertedSample.index = 2;
            break;
        case 'latest - 3':
            convertedSample.index = 3;
            break;
        case 'latest - 4':
            convertedSample.index = 4;
            break;
    }
    return convertedSample;
};
const resolveAtomType = (dbAtom) => {
    // Fail fast if left hand type is expression.
    if (dbAtom.leftHand?.type === 'expression')
        return 'embedded-expression-atom';
    const dbCaseOperator = getFirstCaseOperator(dbAtom);
    if (inNormalRangeOperators.includes(dbCaseOperator))
        return 'hv-normal-range-atom';
    if (AllDeltaLogic.includes(dbCaseOperator))
        return 'delta-hv-atom';
    return 'hv-atom';
};
export const getEvaluationLogicType = (dbAtom, dependencies) => {
    let dbCaseOperator = getFirstCaseOperator(dbAtom);
    if (getBaseDeltaOperator(dbCaseOperator) && !BaseDeltaLogic.includes(dbCaseOperator))
        dbCaseOperator = getDeltaTypeOperator(dbCaseOperator);
    if (SelfOperators.includes(dbCaseOperator))
        return EvaluationLogicTypes.self;
    if (dbCaseOperator === LogicType_Exists)
        return EvaluationLogicTypes.exists;
    if (dbCaseOperator === LogicType_NotExists)
        return EvaluationLogicTypes.notExists;
    if (dbCaseOperator === LogicType_IsChiefComplaint)
        return EvaluationLogicTypes.isChiefComplaint;
    if (inNormalRangeOperators.includes(dbCaseOperator))
        return EvaluationLogicTypes.normalRange;
    // check by left hand variable type
    if (!dbAtom.leftHand)
        throw new BadImplementationException('No left hand value chosen for Atom');
    if (dbAtom.leftHand.type === 'variable') {
        const variableId = dbAtom.leftHand.id;
        const dbVariable = dependencies.variables.find(_var => _var._id === variableId);
        if (!dbVariable)
            throw new BadImplementationException(`No variable found for id ${dbAtom.leftHand.id}`);
        const dbValue = dependencies.values.find(_val => _val._id === dbVariable.valueId);
        if (!dbValue)
            throw new BadImplementationException(`No value found for id ${dbVariable.valueId}`);
        if (NumericDBValueTypes.includes((dbValue.type)))
            return EvaluationLogicTypes.range;
        if (dbCaseOperator === LogicType_NotEquals || dbCaseOperator === LogicType_NotInSet)
            return EvaluationLogicTypes.notEqual;
        if (StringDBValueTypes.includes(dbValue.type))
            return EvaluationLogicTypes.set;
        throw new BadImplementationException('Could not resolve evaluation logic type for HVAtom...');
    }
    if (dbAtom.leftHand.type === 'expression') {
        const dbExpression = dependencies.expressions.find(expr => expr._id === dbAtom.leftHand?.id);
        if (!dbExpression)
            throw new BadImplementationException(`No Db expression found for id ${dbAtom.leftHand.id}`);
        if (numericExpressionTypes.includes(dbExpression.expressionType))
            return EvaluationLogicTypes.range;
        if (booleanExpressionTypes.includes(dbExpression.expressionType))
            return EvaluationLogicTypes.set;
        throw new BadImplementationException('Could not resolve evaluation logic type for Embedded expression atom');
    }
    if (dbAtom.leftHand.type === 'atom') {
        if (RangeOperators.includes(dbCaseOperator))
            return EvaluationLogicTypes.range;
        if (dbCaseOperator === LogicType_NotEquals)
            return EvaluationLogicTypes.notEqual;
        if (InSetOperators.includes(dbCaseOperator))
            return EvaluationLogicTypes.set;
    }
    throw new BadImplementationException(`Cannot resolve evaluation logic type for atom:\n${__stringify(dbAtom, true)}`);
};
// ########################## Single Case Evaluation Logic ##########################
const singleCaseAtoms = (dbAtom, convertedAtom, dependencies) => {
    switch (convertedAtom.type) {
        case 'hv-atom':
        case 'embedded-expression-atom':
        case 'delta-hv-atom':
            resolveSingleCaseAtom(dbAtom, convertedAtom, dependencies);
            break;
        case 'hv-normal-range-atom':
            resolveSingleCaseNormalRangeAtom(dbAtom, convertedAtom, dependencies);
            break;
    }
};
const validateSingleCaseAtom = (dbAtom, evaluationLogicType) => {
    const dbCaseOperator = getFirstCaseOperator(dbAtom);
    if (!AllowedOperators[evaluationLogicType].includes(dbCaseOperator))
        throw new BadImplementationException(`Cannot use the ${dbCaseOperator} operator with the selected variable!`);
};
const resolveSingleCaseAtom = (dbAtom, convertedAtom, dependencies) => {
    const evaluationLogicType = getEvaluationLogicType(dbAtom, dependencies);
    validateSingleCaseAtom(dbAtom, evaluationLogicType);
    switch (evaluationLogicType) {
        case EvaluationLogicTypes.self:
            convertedAtom.evaluation_logic = resolveSelf(dbAtom, dependencies);
            break;
        case EvaluationLogicTypes.set:
            convertedAtom.evaluation_logic = resolveInSet(dbAtom, dependencies);
            break;
        case EvaluationLogicTypes.range:
            convertedAtom.evaluation_logic = resolveRangeEvaluationLogic(dbAtom, dependencies);
            break;
        case EvaluationLogicTypes.notEqual:
            convertedAtom.evaluation_logic = resolveNotEqual(dbAtom, dependencies);
            break;
        case EvaluationLogicTypes.exists:
            convertedAtom.evaluation_logic = resolveExists();
            break;
        case EvaluationLogicTypes.notExists:
            convertedAtom.evaluation_logic = resolveNotExists();
            break;
        case EvaluationLogicTypes.isChiefComplaint:
            convertedAtom.evaluation_logic = resolveIsChiefComplaint();
            break;
    }
};
const resolveSingleCaseNormalRangeAtom = (dbAtom, convertedAtom, dependencies) => {
    validateSingleCaseAtom(dbAtom, 'normal-range');
    resolveBaseNormalRangeAtom(dbAtom, convertedAtom, dependencies);
    convertedAtom.evaluation_logic = resolveNormalRange(dbAtom);
};
// ########################## Conversion Atoms Evaluation Logic ##########################
const conversionAtoms = (dbAtom, convertedAtom, dependencies) => {
    switch (convertedAtom.type) {
        case 'delta-hv-atom':
        case 'embedded-expression-atom':
        case 'hv-atom':
            resolveConversionAtom(dbAtom, convertedAtom, dependencies);
            break;
        case 'hv-normal-range-atom':
            resolveConversionNormalRangeAtom(dbAtom, convertedAtom, dependencies);
            break;
    }
};
const resolveConversionAtom = (dbAtom, convertedAtom, dependencies) => {
    const conversionType = getEvaluationLogicType(dbAtom, dependencies);
    if (!conversionType)
        return;
    const baseEvaluationLogic = resolveConversionBaseEvaluationLogic(dbAtom, conversionType);
    switch (conversionType) {
        case EvaluationLogicTypes.set:
            convertedAtom.evaluation_logic = resolveInSetConversion(dbAtom, baseEvaluationLogic, dependencies);
            break;
        case EvaluationLogicTypes.notEqual:
            convertedAtom.evaluation_logic = resolveNotEqualConversion(dbAtom, baseEvaluationLogic, dependencies);
            break;
        case EvaluationLogicTypes.range:
            convertedAtom.evaluation_logic = resolveInRangeConversion(dbAtom, baseEvaluationLogic, dependencies);
            break;
    }
};
const resolveConversionNormalRangeAtom = (dbAtom, convertedAtom, dependencies) => {
    resolveBaseNormalRangeAtom(dbAtom, convertedAtom, dependencies);
    const baseEvaluationLogic = resolveConversionBaseEvaluationLogic(dbAtom, 'normal-range');
    convertedAtom.evaluation_logic = resolveNormalRangeConversion(dbAtom, baseEvaluationLogic);
};
const getFirstCaseOperator = (dbAtom) => {
    const dbCase = dbAtom.cases[0];
    if (!dbCase.caseOperator)
        throw new BadImplementationException(`No operator chosen for dbAtom case!`);
    return dbCase.caseOperator;
};
//# sourceMappingURL=convertAtomLevel.js.map